import { Component, computed, input, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FilterHint,
  TableFilterEvent,
  TableSortEvent,
} from '../../../shared/components/table/table.component';
import { fromStore } from 'angular/app/core/store';
import { map, Observable, takeUntil } from 'rxjs';
import {
  BlockType,
  BlockTypeHumanized,
  Booking,
  BookingStatus,
  BookingStatusHumanized,
  RateType,
} from '../../../core/models/entities/booking';
import { Store } from '@ngrx/store';
import { BookingDetailsModalComponent } from '../booking-details-modal/booking-details-modal.component';
import { ModalService } from '../../../core/services/modal.service';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import {
  BookingsScope,
  BookingsState,
} from '../../../core/store/bookings/bookings.state';
import { bookingsActions } from 'angular/app/core/store/bookings';
import { format, isSameDay, parseISO } from 'date-fns';
import {
  selectAllRooms,
  selectBookingsFilter,
} from '../../../core/store/reducers';
import { InputOptionsHelper } from '../../../core/helpers/input-options.helper';
import { UntypedFormControl } from '@angular/forms';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { selectRoomById } from '../../../core/store/rooms/rooms.reducer';
import { Room } from '../../../core/models/entities/room';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-bookings-list-view',
  animations: [fadeAnimation],
  template: `
    <app-table
      [data]="bookings()"
      [loading]="loadingBookings()"
      [trackByFn]="trackByBookingId"
      [isRowClickable]="isBookingClickable"
      [emptyStateTitle]="'bookings.list-view.empty-state-title' | transloco"
      [emptyStateMessage]="'bookings.list-view.empty-state-message' | transloco"
      emptyStateIcon="far fa-calendar"
      [emptyStateAction]="hasActiveFilters() ? ('bookings.list-view.empty-state-reset-filters' | transloco) : null"
      [emptyStateActionHint]="activeFiltersDescription()"
      (emptyStateActionClicked)="resetFilters()"
      (emptyStateHintDismissed)="onHintDismissed($event)"
      [showPagination]="true"
      [currentPage]="page$ | async"
      [pageSize]="25"
      [totalItems]="collectionSize$ | async"
      (pageChanged)="changePage($event.page)"
      (rowClicked)="openBooking($event)"
      (filterChanged)="onFilterChanged($event)"
      (sortChanged)="onSortChanged($event)"
      [currentSort]="currentSort()"
    >
      <!-- Reference Column -->
      <app-table-column
        field="reference"
        [header]="'bookings.list-view.column-reference' | transloco"
        width="130px"
        wordBreak="break-all"
      >
        <ng-template let-booking>
          {{ booking.reference }}
        </ng-template>
      </app-table-column>

      <!-- Reservation Column -->
      <app-table-column
        field="name"
        [header]="'bookings.list-view.column-reservation' | transloco"
        width="300px"
        [showLabelOnMobile]="false"
        [alwaysVisibleOnMobile]="true"
      >
        <ng-template let-booking>
          @if (booking.is_public) {
            <div>
              @if (booking.company) {
                <div class="font-bold">{{ booking.company }}</div>
              }
              <div [class.font-bold]="!booking.company">
                {{
                  booking.full_name === ' ' ? booking.name : booking.full_name
                }}
              </div>
            </div>
          } @else {
            <div>
              <div class="font-bold">{{ booking.name }}</div>
              <div>
                {{ booking.full_name }}
              </div>
            </div>
          }
        </ng-template>
      </app-table-column>

      <!-- Room Column -->
      <app-table-column
        field="room_name"
        [header]="'bookings.list-view.column-room' | transloco"
        [filterable]="true"
        [filterOptions]="roomsOptions$ | async"
        [filterValues]="roomsFilterValues$ | async"
      >
        <ng-template let-booking>
          {{ selectRoomById(booking?.room_id)()?.name }}
        </ng-template>
      </app-table-column>

      <!-- Date Column -->
      <app-table-column
        [alwaysVisibleOnMobile]="true"
        [showLabelOnMobile]="false"
        field="start"
        [header]="'bookings.list-view.column-date' | transloco"
        [sortable]="true"
      >
        <ng-template let-booking>
          <div>
            {{ booking?.start | date: "EEE dd.MM.yyyy, HH'h'mm" }}
            @if (booking && isSameDayOrNextMorning(booking)) {
              <span class="whitespace-nowrap">
                {{ 'common.time-range-separator' | transloco }}
                {{ booking?.end | date: "HH'h'mm" }}
                @if (isNextMorning(booking)) {
                  <span class="text-gray-muted">(+1)</span>
                }
                @if (booking?.recurrence_rule) {
                  <i class="far fa-repeat ml-1 text-gray-400"></i>
                }
              </span>
            } @else {
              <div class="whitespace-nowrap">
                {{ booking?.end | date: "EEE dd.MM.yyyy, HH'h'mm" }}
                @if (booking?.recurrence_rule) {
                  <i class="far fa-repeat ml-1 text-gray-400"></i>
                }
              </div>
            }
          </div>
        </ng-template>
      </app-table-column>

      <!-- Status Column -->
      <app-table-column
        field="status"
        [header]="'bookings.list-view.column-status' | transloco"
        [filterable]="true"
        [filterOptions]="statusOptions()"
        [filterValues]="statusFilterValues$ | async"
        [defaultFilterValues]="[
          BookingStatus.Pending,
          BookingStatus.Confirmed,
          BookingStatus.PendingPayment,
        ]"
        [showLabelOnMobile]="false"
        [alwaysVisibleOnMobile]="true"
      >
        <ng-template let-booking>
          <app-badge
            [variant]="BookingStatusHumanized[booking.status].color"
            size="sm"
          >
            {{ BookingStatusHumanized[booking.status].labelKey | transloco }}
          </app-badge>
        </ng-template>
      </app-table-column>

      <!-- Type Column -->
      <app-table-column
        field="block_type"
        [header]="'bookings.list-view.column-type' | transloco"
        [filterable]="true"
        [filterOptions]="blockTypeOptions()"
        [filterValues]="blockTypesFilterValues$ | async"
        [defaultFilterValues]="[BlockType.Booking]"
      >
        <ng-template let-booking>
          {{ BlockTypeHumanized[booking.block_type].labelKey | transloco }}
        </ng-template>
      </app-table-column>

      <!-- Origine Column -->
      <app-table-column
        field="is_public"
        [header]="'bookings.list-view.column-origin' | transloco"
        [filterable]="true"
        [filterOptions]="originOptions$ | async"
        [filterValues]="originFilterValues$ | async"
      >
        <ng-template let-booking>
          @if (booking.is_public) {
            <app-badge variant="info" size="sm">{{ 'bookings.list-view.filter-origin-public' | transloco }}</app-badge>
          } @else {
            <app-badge variant="gray" size="sm">{{ 'bookings.list-view.filter-origin-internal' | transloco }}</app-badge>
          }
        </ng-template>
      </app-table-column>
    </app-table>
  `,
  standalone: false,
})
export class BookingsListViewComponent
  extends DestroyableComponent
  implements OnInit
{
  scope = input<BookingsScope>();
  private bookingsState = this.store.selectSignal(
    fromStore.selectBookingsState,
  );
  bookings: Signal<Booking[]> = computed(() =>
    this.scope()
      ? this.store
          .selectSignal(fromStore.selectAllBookings(this.scope()))()
          ?.filter((booking) => !!booking)
      : [],
  );
  loadingBookings: Signal<boolean> = computed(() =>
    this.scope()
      ? this.store.selectSignal(fromStore.selectBookingsLoading(this.scope()))()
      : true,
  );
  page$: Observable<number>;
  collectionSize$: Observable<number>;
  BookingStatusHumanized = BookingStatusHumanized;
  BookingStatus = BookingStatus;
  private allRooms = this.store.selectSignal(selectAllRooms);
  roomsOptions$ = InputOptionsHelper.selectOptions$(
    this.store
      .select(selectAllRooms)
      .pipe(map((rooms) => rooms.filter((room) => room.active))),
  );
  roomsFilterValues$: Observable<number[]>;
  statusOptions = computed(() => {
    this.currentLang();
    return Object.values(BookingStatus)
      .filter((value) => ![BookingStatus.PaymentFailed].includes(value))
      .map((value) => ({
        label: this.translocoService.translate(BookingStatusHumanized[value].labelKey),
        value,
      }));
  });
  statusFilterValues$: Observable<BookingStatus[]> = this.store
    .select(selectBookingsFilter(this.scope()))
    .pipe(map((bookingsFilter) => bookingsFilter.status));
  blockTypeOptions = computed(() => {
    this.currentLang();
    return Object.values(BlockType).map((value) => ({
      label: this.translocoService.translate(BlockTypeHumanized[value].labelKey),
      value,
    }));
  });
  blockTypesFilterValues$: Observable<BlockType[]>;
  BlockType = BlockType;
  BlockTypeHumanized = BlockTypeHumanized;
  originOptions$: Observable<{ label: string; value: boolean }[]>;
  originFilterValues$: Observable<boolean[]>;
  orderControl = new UntypedFormControl();

  private currentLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  private readonly defaultStatus = [
    BookingStatus.Confirmed,
    BookingStatus.Pending,
    BookingStatus.PendingPayment,
  ];
  private readonly defaultBlockTypes = [BlockType.Booking];

  activeFiltersDescription = computed((): FilterHint[] | null => {
    // Trigger re-computation on language change
    this.currentLang();

    const state = this.bookingsState()[this.scope()];
    if (!state) return null;

    const hints: FilterHint[] = [];
    const { bookingsFilter, roomsFilter, search, from, to } = state;

    if (search) hints.push({ label: `Recherche « ${search} »`, key: 'search' });
    if (from || to) {
      const fromLabel = from ? format(parseISO(from), 'dd.MM.yyyy') : '…';
      const toLabel = to ? format(parseISO(to), 'dd.MM.yyyy') : '…';
      hints.push({ label: `${fromLabel} – ${toLabel}`, key: 'dateRange' });
    }
    if (roomsFilter.ids?.length > 0) {
      const roomNames = this.allRooms()
        .filter((room) => roomsFilter.ids.includes(room.id))
        .map((room) => room.name)
        .join(', ');
      hints.push({ label: roomNames || this.translocoService.translate('bookings.list-view.filter-hint-room'), key: 'rooms' });
    }

    const allStatuses = Object.values(BookingStatus).filter(
      (s) => s !== BookingStatus.PaymentFailed,
    );
    const statusChanged =
      bookingsFilter.status.length !== this.defaultStatus.length ||
      bookingsFilter.status.some((s) => !this.defaultStatus.includes(s));
    const statusIsRestricted = bookingsFilter.status.length < allStatuses.length;
    if (statusChanged || (!!search && statusIsRestricted)) {
      const statusLabel = bookingsFilter.status
        .map((s) => this.translocoService.translate(BookingStatusHumanized[s].labelKey))
        .join(', ');
      hints.push({ label: statusLabel, key: 'status' });
    }

    const blockTypesChanged =
      bookingsFilter.blockTypes.length !== this.defaultBlockTypes.length ||
      bookingsFilter.blockTypes.some(
        (b) => !this.defaultBlockTypes.includes(b),
      );
    if (blockTypesChanged) {
      const blockTypeLabel = bookingsFilter.blockTypes
        .map((b) => this.translocoService.translate(BlockTypeHumanized[b].labelKey))
        .join(', ');
      hints.push({ label: blockTypeLabel, key: 'blockTypes' });
    }

    if (bookingsFilter.isPublic !== null) {
      const originLabel = bookingsFilter.isPublic
        .map((v) => (v ? this.translocoService.translate('bookings.list-view.filter-origin-public') : this.translocoService.translate('bookings.list-view.filter-origin-internal')))
        .join(', ');
      hints.push({ label: originLabel || this.translocoService.translate('bookings.list-view.filter-hint-origin'), key: 'origin' });
    }

    return hints.length > 0 ? hints : null;
  });

  showSearchAllStatuses = computed(() => {
    const state = this.bookingsState()[this.scope()];
    if (!state?.search) return false;
    const allStatuses = Object.values(BookingStatus).filter(
      (s) => s !== BookingStatus.PaymentFailed,
    );
    return state.bookingsFilter.status.length < allStatuses.length;
  });

  hasActiveFilters = computed(() => {
    const state = this.bookingsState()[this.scope()];
    if (!state) return false;

    const { bookingsFilter, roomsFilter, search, from, to } = state;

    const statusChanged =
      bookingsFilter.status.length !== this.defaultStatus.length ||
      bookingsFilter.status.some((s) => !this.defaultStatus.includes(s));
    const blockTypesChanged =
      bookingsFilter.blockTypes.length !== this.defaultBlockTypes.length ||
      bookingsFilter.blockTypes.some(
        (b) => !this.defaultBlockTypes.includes(b),
      );
    const isPublicChanged = bookingsFilter.isPublic !== null;
    const roomsChanged = roomsFilter.ids !== null && roomsFilter.ids?.length > 0;
    const searchChanged = !!search;
    const dateRangeChanged = !!from || !!to;

    return (
      statusChanged ||
      blockTypesChanged ||
      isPublicChanged ||
      roomsChanged ||
      searchChanged ||
      dateRangeChanged
    );
  });

  currentSort = computed(() => {
    const order = this.bookingsState()[this.scope()].order;
    if (order && order.length > 0) {
      const firstOrder = order[0];
      return {
        field: firstOrder.field,
        direction: firstOrder.order as 'asc' | 'desc' | null,
      };
    }
    return { field: '', direction: null };
  });

  constructor(
    private store: Store,
    private modalService: ModalService,
    private translocoService: TranslocoService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.roomsFilterValues$ = this.store
      .select(fromStore.selectBookingsState)
      .pipe(
        map((bookingsState) => bookingsState[this.scope()].roomsFilter.ids),
        takeUntil(this.onDestroy$),
      );
    this.statusFilterValues$ = this.store
      .select(fromStore.selectBookingsState)
      .pipe(
        map(
          (bookingsState) => bookingsState[this.scope()].bookingsFilter.status,
        ),
        takeUntil(this.onDestroy$),
      );
    this.blockTypesFilterValues$ = this.store
      .select(fromStore.selectBookingsState)
      .pipe(
        map(
          (bookingsState) =>
            bookingsState[this.scope()].bookingsFilter.blockTypes,
        ),
        takeUntil(this.onDestroy$),
      );
    this.originFilterValues$ = this.store
      .select(fromStore.selectBookingsState)
      .pipe(
        map(
          (bookingsState) =>
            bookingsState[this.scope()].bookingsFilter.isPublic ?? [],
        ),
        takeUntil(this.onDestroy$),
      );

    // Initialize origin filter options with translations
    this.originOptions$ = this.translocoService.selectTranslation().pipe(
      map(() => [
        { label: this.translocoService.translate('bookings.list-view.filter-origin-public'), value: true },
        { label: this.translocoService.translate('bookings.list-view.filter-origin-internal'), value: false },
      ]),
      takeUntil(this.onDestroy$),
    );

    this.orderControl.patchValue(this.bookingsState()[this.scope()].order);
    this.page$ = this.store
      .select(fromStore.selectBookingsPage(this.scope()))
      .pipe(takeUntil(this.onDestroy$));
    this.collectionSize$ = this.store
      .select(fromStore.selectBookingsState)
      .pipe(
        map(
          (bookingsState: BookingsState) =>
            bookingsState[this.scope()].entitiesCount,
        ),
        takeUntil(this.onDestroy$),
      );

    this.orderControl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((order) =>
        this.store.dispatch(
          bookingsActions.setOrder({ order, scope: this.scope() }),
        ),
      );
  }

  selectRoomById(id: number): Signal<Room> {
    return this.store.selectSignal(selectRoomById(id));
  }

  isSameDayOrNextMorning(booking: Booking): boolean {
    return (
      (booking?.duration.rate === RateType.Daily &&
        booking.duration.quantity === 1) ||
      (booking?.duration.rate === RateType.Hourly &&
        booking.duration.quantity < 24)
    );
  }

  isNextMorning(booking: Booking): boolean {
    if (this.isSameDayOrNextMorning(booking)) {
      return !isSameDay(parseISO(booking.end), parseISO(booking.start));
    } else {
      return false;
    }
  }

  trackByBookingId = (booking: Booking): number => booking.id;

  isBookingClickable = (booking: Booking): boolean =>
    booking.block_type === BlockType.Booking;

  changePage(page: number) {
    this.store.dispatch(bookingsActions.setPage({ page, scope: this.scope() }));
  }

  openBooking(booking: Booking) {
    const instance = this.modalService.open(BookingDetailsModalComponent, {});
    (instance.componentRef.instance as BookingDetailsModalComponent).bookingId =
      booking.id;
    (
      instance.componentRef.instance as BookingDetailsModalComponent
    ).displayCalendarButton = true;
  }

  roomsFilterChanged(values: number[]) {
    this.store.dispatch(
      bookingsActions.setRoomsFilter({
        filter: { ids: values },
        scope: this.scope(),
      }),
    );
  }

  statusFilterChanged(values: BookingStatus[]) {
    if (values.length === 0) {
      values = [BookingStatus.Pending, BookingStatus.Confirmed];
    }
    const bookingsFilter = this.bookingsState()[this.scope()].bookingsFilter;
    this.store.dispatch(
      bookingsActions.setBookingsFilter({
        filter: {
          status: values,
          blockTypes: bookingsFilter.blockTypes,
          isPublic: bookingsFilter.isPublic,
        },
        scope: this.scope(),
      }),
    );
  }

  blockTypesFilterChanged(values: BlockType[]) {
    if (values.length === 0) {
      values = [BlockType.Booking];
    }
    const bookingsFilter = this.bookingsState()[this.scope()].bookingsFilter;
    this.store.dispatch(
      bookingsActions.setBookingsFilter({
        filter: {
          status: bookingsFilter.status,
          blockTypes: values,
          isPublic: bookingsFilter.isPublic,
        },
        scope: this.scope(),
      }),
    );
  }

  originFilterChanged(values: boolean[]) {
    const bookingsFilter = this.bookingsState()[this.scope()].bookingsFilter;
    this.store.dispatch(
      bookingsActions.setBookingsFilter({
        filter: {
          status: bookingsFilter.status,
          blockTypes: bookingsFilter.blockTypes,
          isPublic: values.length > 0 ? values : null,
        },
        scope: this.scope(),
      }),
    );
  }

  onFilterChanged(event: TableFilterEvent) {
    if (event.field === 'room_name') {
      this.roomsFilterChanged(event.values);
    } else if (event.field === 'status') {
      this.statusFilterChanged(event.values);
    } else if (event.field === 'block_type') {
      this.blockTypesFilterChanged(event.values);
    } else if (event.field === 'is_public') {
      this.originFilterChanged(event.values);
    }
  }

  onSortChanged(event: TableSortEvent) {
    const order = event.direction
      ? [{ field: event.field, order: event.direction as any }]
      : [];
    this.store.dispatch(
      bookingsActions.setOrder({ order, scope: this.scope() }),
    );
  }

  onHintDismissed(key: string) {
    const scope = this.scope();
    const { bookingsFilter } = this.bookingsState()[scope];

    switch (key) {
      case 'search':
        this.store.dispatch(bookingsActions.setSearch({ search: '', scope }));
        break;
      case 'dateRange':
        this.store.dispatch(bookingsActions.setDatesRange({ dates: [], scope }));
        break;
      case 'rooms':
        this.store.dispatch(bookingsActions.setRoomsFilter({ filter: { ids: [] }, scope }));
        break;
      case 'status': {
        const currentStatuses = bookingsFilter.status;
        const isDefaultStatuses =
          currentStatuses.length === this.defaultStatus.length &&
          currentStatuses.every((s) => this.defaultStatus.includes(s));
        if (isDefaultStatuses) {
          this.searchAllStatuses();
        } else {
          this.statusFilterChanged(this.defaultStatus);
        }
        break;
      }
      case 'blockTypes':
        this.store.dispatch(
          bookingsActions.setBookingsFilter({
            filter: { ...bookingsFilter, blockTypes: this.defaultBlockTypes },
            scope,
          }),
        );
        break;
      case 'origin':
        this.store.dispatch(
          bookingsActions.setBookingsFilter({
            filter: { ...bookingsFilter, isPublic: null },
            scope,
          }),
        );
        break;
    }
  }

  searchAllStatuses() {
    const allStatuses = Object.values(BookingStatus).filter(
      (s) => s !== BookingStatus.PaymentFailed,
    );
    const { blockTypes, isPublic } =
      this.bookingsState()[this.scope()].bookingsFilter;
    this.store.dispatch(
      bookingsActions.setBookingsFilter({
        filter: { status: allStatuses, blockTypes, isPublic },
        scope: this.scope(),
      }),
    );
  }

  resetFilters() {
    this.store.dispatch(bookingsActions.resetFilters());
  }
}
